import { keepPreviousData, skipToken, useQuery } from "@tanstack/react-query";
import { queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import {
  getFleetClientEndpoint,
  listFleetClientsEndpoint,
  listFleetClientVersionsEndpoint,
  type ClientDetail,
  type ClientSummary,
  type ClientVersionSummary,
} from "../../client-messages/clients";
import { useCursorPager } from "./cursor-pager";
import type { UseActivationsResult } from "./hooks";

const listFleetClientsQuery = queryFor(listFleetClientsEndpoint);
const getFleetClientQuery = queryFor(getFleetClientEndpoint);
const listFleetClientVersionsQuery = queryFor(listFleetClientVersionsEndpoint);

/** Rows per page of the client list. The endpoint caps at 100. */
export const CLIENTS_PAGE_SIZE = 25;

export interface FleetClientFilters {
  /** The start of a client number. Absent or blank lists every client. */
  readonly clientNo?: string;
}

/** A page of clients, held and paged exactly as a page of activations is. */
export type UseClientsResult = UseActivationsResult<ClientSummary>;

/**
 * One page of the fleet's Patriot clients in client-number order, narrowed to
 * numbers starting with `clientNo`. Changing it returns to page one.
 */
export function useFleetClients(filters: FleetClientFilters): UseClientsResult {
  const apiConfig = useApiConfig();
  const clientNo = filters.clientNo?.trim() ?? "";
  const pager = useCursorPager(clientNo);

  const query = useQuery({
    ...listFleetClientsQuery(
      apiConfig,
      {},
      {
        limit: CLIENTS_PAGE_SIZE,
        ...(clientNo !== "" && { clientNo }),
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      },
    ),
    placeholderData: keepPreviousData,
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    // As in `useTenantActivations`: a placeholder's cursor belongs to the page being left.
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}

export interface UseClientResult {
  readonly client: ClientDetail | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/**
 * One client by client number: as it is now, or as it was in the version that began at `validFrom`.
 */
export function useFleetClient(clientNo: string, validFrom?: string): UseClientResult {
  const apiConfig = useApiConfig();

  // Not `enabled`: the query key is built, and its path validated, even for a disabled query.
  const query = useQuery(
    clientNo === "" ?
      { queryKey: [], queryFn: skipToken }
    : getFleetClientQuery(apiConfig, { clientNo }, validFrom === undefined ? {} : { version: validFrom }),
  );

  return {
    client: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

/** Rows per page of a client's version history. */
export const CLIENT_VERSIONS_PAGE_SIZE = 10;

/** `?version=` naming the client as it is now, rather than one version. */
export const LATEST_CLIENT_VERSION = "latest";

/**
 * What a client page's `?version=` asks for. Absent, the page has not said, so
 * it should say `latest` in the URL before showing anything. A time names the
 * version that began then; anything else is not a version.
 */
export type ClientVersionParam =
  | { readonly kind: "unset" }
  | { readonly kind: "latest" }
  | { readonly kind: "at"; readonly validFrom: string }
  | { readonly kind: "invalid" };

export function parseClientVersionParam(param: string | undefined): ClientVersionParam {
  if (param === undefined || param === "") return { kind: "unset" };
  if (param === LATEST_CLIENT_VERSION) return { kind: "latest" };
  const at = Date.parse(param);
  return Number.isNaN(at) ? { kind: "invalid" } : { kind: "at", validFrom: new Date(at).toISOString() };
}

/** A page of one client's versions, newest first, held and paged as a page of clients is. */
export type UseClientVersionsResult = UseActivationsResult<ClientVersionSummary>;

export function useFleetClientVersions(clientNo: string): UseClientVersionsResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(clientNo);

  const query = useQuery({
    ...listFleetClientVersionsQuery(
      apiConfig,
      { clientNo },
      { limit: CLIENT_VERSIONS_PAGE_SIZE, ...(pager.cursor !== undefined && { cursor: pager.cursor }) },
    ),
    enabled: clientNo !== "",
    placeholderData: keepPreviousData,
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}
